/**
 * The import report (W2-09 publish-import; DESIGN.md §4.17; DIALOG.importReport):
 *
 *   Imported "Song.pez"   RPE · RPEVersion 150 · 31 lines · 1,204 notes · 4,880 events · manifest: info.yml
 *   Phira would load this file: yes
 *   LOADED EXACTLY / NORMALISED / CHANGED / KEPT BUT FLAGGED / NOT USED BY PHIRA, KEPT FOR RPE   (entries with counts)
 *   Phira's own import warnings
 *   Open as (o) new project ( ) add into the current project at beat [0:0/1], placement [automatic]
 *   [Copy report] [Save report]                                                [Cancel] [Open]
 *
 * The dialog only decides (the import queue creates or merges). "Open and show in Problems" opens the Problems panel
 * after the project. `ReportView` is shared with the summary of several imports.
 */

import { useState, type ReactNode } from 'react';
import type { AppServices, DialogProps, IconName } from '../../app/registry/types';
import type { ImportClass } from '../../core/ir/types';
import type { ChartDoc } from '../../core/model/types';
import { Beats } from '../../core/time/beat';
import type { Beat } from '../../core/time/types';
import type { ImportReport } from '../../formats/types';
import { BeatField } from '../../ui/BeatField';
import { Button } from '../../ui/Button';
import { Dialog } from '../../ui/Dialog';
import { Icon } from '../../ui/Icon';
import { Select } from '../../ui/Select';
import { useText, type Text } from './hooks';
import { m } from './import.messages';
import {
  CLASS_ORDER,
  CLASS_TITLES,
  copyText,
  downloadText,
  featureLine,
  groupEntries,
  missingCount,
  reportFileName,
  reportSummary,
  reportText,
} from './importFlow';
import { importQueue } from './importQueue';
import { MERGE_ALIGNS, readMergeAlign, writeMergeAlign, type MergeAlign } from './mergeFlow';
import s from './Import.module.css';

/** Entries shown per class (the copied / saved report has all of them). */
export const ENTRY_LIMIT = 200;

const CLASS_ICONS: Readonly<Record<ImportClass, IconName>> = {
  exact: 'CircleCheck',
  normalized: 'RefreshCw',
  changed: 'Wrench',
  flagged: 'Flag',
  rpeOnly: 'Archive',
};

const ALIGN_TITLES: Readonly<Record<MergeAlign, string>> = {
  auto: m.alignAuto,
  beats: m.alignBeats,
  musicTime: m.alignMusic,
};

/** The report body: summary, whether Phira would load it, the entries by class and Phira's own warnings. */
export function ReportView(props: {
  readonly text: Text;
  readonly report: ImportReport;
  readonly missing: number;
  /** The imported document: its special features are listed under the summary (UX-087). */
  readonly doc?: ChartDoc;
  readonly headingLevel?: 3 | 4;
  /** Extra controls after a class's entries (e.g. "Open and show in Problems" under Kept but flagged). */
  readonly after?: Partial<Record<ImportClass, ReactNode>>;
}) {
  const { text, report } = props;
  const groups = groupEntries(report.entries);
  const H = props.headingLevel === 4 ? 'h4' : 'h3';
  const features = props.doc === undefined ? null : featureLine(text, props.doc);
  return (
    <div className={s.classes}>
      <p className={s.summaryLine}>{reportSummary(text, report)}</p>
      {features !== null && (
        <p className={s.summaryLine} data-testid="import-features">
          {features}
        </p>
      )}
      <p className={s.status} data-ok={String(report.phiraWouldLoad)}>
        <Icon name={report.phiraWouldLoad ? 'CircleCheck' : 'OctagonX'} size={16} />
        <span>{text.t(report.phiraWouldLoad ? m.phiraLoads : m.phiraRefuses)}</span>
      </p>
      <p className={s.summaryLine}>
        {text.t(m.exactSummary, {
          events: report.counts.events,
          notes: report.counts.notes,
          files: report.counts.assets,
          missing: props.missing,
        })}
      </p>
      {CLASS_ORDER.map((cls) => {
        const list = groups[cls];
        if (list.length === 0) return null;
        const shown = list.slice(0, ENTRY_LIMIT);
        return (
          <section key={cls} className={s.classBlock} data-class={cls}>
            <H className={s.classTitle}>
              <Icon name={CLASS_ICONS[cls]} size={14} />
              {text.t(CLASS_TITLES[cls])} ({list.length})
            </H>
            <ul className={s.entries} aria-label={text.t(CLASS_TITLES[cls])}>
              {shown.map((e, i) => (
                <li key={`${e.message.key}:${String(i)}`} className={s.entry}>
                  <span>{text.tr(e.message)}</span>
                  {(e.count !== undefined && e.count > 1) || e.line !== undefined || e.path !== undefined ? (
                    <span className={s.entryWhere}>
                      {[
                        e.count !== undefined && e.count > 1 ? text.t(m.entryTimes, { count: e.count }) : null,
                        e.line !== undefined ? text.t(m.entryLine, { line: e.line }) : null,
                        e.path ?? null,
                      ]
                        .filter((x): x is string => x !== null && x !== '')
                        .join(' · ')}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
            {list.length > shown.length && (
              <span className={s.muted}>{text.t(m.moreEntries, { count: list.length - shown.length })}</span>
            )}
            {props.after?.[cls]}
          </section>
        );
      })}
      {report.phiraImportWarnings.length > 0 && (
        <section className={s.classBlock} data-class="flagged">
          <H className={s.classTitle}>
            <Icon name="TriangleAlert" size={14} />
            {text.t(m.phiraWarnings)}
          </H>
          <ul className={s.entries} aria-label={text.t(m.phiraWarnings)}>
            {report.phiraImportWarnings.map((w, i) => (
              <li key={`${w.key}:${String(i)}`} className={s.entry}>
                {text.tr(w)}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/** Copy / Save of a report (plain text). */
export function ReportActions(props: {
  readonly services: AppServices;
  readonly text: Text;
  readonly name: string;
  readonly content: () => string;
}) {
  const { services, text } = props;
  return (
    <>
      <Button
        icon="Copy"
        onClick={() => {
          void copyText(props.content()).then((ok) => {
            services.ui.toast({ key: ok ? m.reportCopied : m.copyFailed }, { kind: ok ? 'info' : 'error' });
          });
        }}
      >
        {text.t(m.copyReport)}
      </Button>
      <Button
        icon="Download"
        onClick={() => {
          downloadText(props.content(), reportFileName(text, props.name));
        }}
      >
        {text.t(m.saveReport)}
      </Button>
    </>
  );
}

/** The beat at the playhead on the editor's snap grid (0 without an engine). */
export function playheadBeat(services: AppServices): Beat {
  const engine = services.engine;
  if (engine === null) return Beats.ZERO;
  try {
    const b = engine.tempo.beatAt(Math.max(0, services.audio.transport.chartTime()));
    const snapped = Beats.snap(b, Math.max(1, services.editor.getState().snap.div));
    return Beats.cmp(snapped, Beats.ZERO) < 0 ? Beats.ZERO : snapped;
  } catch {
    return Beats.ZERO;
  }
}

/** `DIALOG.importReport` (modal). */
export function ImportReportDialog(props: DialogProps<'importReport'>) {
  const { services } = props;
  const { result, canMerge } = props.props;
  const text = useText(services);
  const report = result.report;
  const missing = missingCount(result.doc);
  const [action, setAction] = useState<'open' | 'merge'>('open');
  const [beat, setBeat] = useState<Beat>(Beats.ZERO);
  const [align, setAlign] = useState<MergeAlign>(() => readMergeAlign(services.settings));
  const merging = canMerge && action === 'merge';
  const close = (r: Parameters<typeof props.close>[0]): void => {
    props.close(r);
  };
  const confirm = (): void => {
    if (merging) {
      writeMergeAlign(services.settings, align);
      close({ action: 'merge', beatOffset: beat, align });
    } else {
      close({ action: 'open' });
    }
  };
  const settings = services.settings.value;
  const flaggedAfter = report.entries.some((e) => e.cls === 'flagged') ? (
    <div className={s.afterRow}>
      <Button
        size="sm"
        variant="ghost"
        icon="ListChecks"
        onClick={() => {
          importQueue.requestProblemsAfterOpen();
          confirm();
        }}
      >
        {text.t(m.showInProblems)}
      </Button>
    </div>
  ) : null;
  return (
    <Dialog
      open
      size="lg"
      title={text.t(m.reportTitle, { name: report.fileName })}
      onClose={() => {
        close(null);
      }}
      footer={
        <div className={s.footer}>
          <div className={s.footerStart}>
            <ReportActions
              services={services}
              text={text}
              name={report.fileName}
              content={() => reportText(text, report, missing)}
            />
          </div>
          <Button
            variant="ghost"
            onClick={() => {
              close(null);
            }}
          >
            {text.t(m.cancel)}
          </Button>
          <Button variant="primary" icon={merging ? 'Merge' : 'FolderOpen'} onClick={confirm}>
            {text.t(merging ? m.add : m.open)}
          </Button>
        </div>
      }
    >
      <div className={s.dialog}>
        <ReportView text={text} report={report} missing={missing} doc={result.doc} after={{ flagged: flaggedAfter }} />
        {/* A choice only when there is one: from the Library the chart opens as a new project (UX-081). */}
        {canMerge && (
          <fieldset className={s.radioList}>
            <legend className={s.radioLegend}>{text.t(m.openAs)}</legend>
            <label className={s.radio}>
              <input
                type="radio"
                name="import-open-as"
                checked={!merging}
                onChange={() => {
                  setAction('open');
                }}
              />
              <span className={s.radioText}>
                <span className={s.radioLabel}>{text.t(m.openNew)}</span>
                <span className={s.radioHelp}>{text.t(m.openNewHelp)}</span>
              </span>
            </label>
            <label className={s.radio}>
              <input
                type="radio"
                name="import-open-as"
                checked={merging}
                onChange={() => {
                  setAction('merge');
                }}
              />
              <span className={s.radioText}>
                <span className={s.radioLabel}>{text.t(m.openMerge)}</span>
                <span className={s.radioHelp}>{text.t(m.openMergeHelp)}</span>
              </span>
            </label>
          </fieldset>
        )}
        {merging && (
          <div className={s.mergeOptions}>
            <BeatField
              label={text.t(m.mergeBeat)}
              value={{ kind: 'value', value: beat }}
              ctx={{
                snapDiv: services.editor.getState().snap.div,
                syntax: settings.editing.beatSyntax,
                current: beat,
                ...(services.engine !== null ? { tempo: services.engine.tempo } : {}),
              }}
              echo
              onCommit={(b, relative) => {
                const next = relative ? Beats.add(beat, b) : b;
                setBeat(Beats.cmp(next, Beats.ZERO) < 0 ? Beats.ZERO : next);
              }}
            />
            <Button
              size="sm"
              icon="LocateFixed"
              onClick={() => {
                setBeat(playheadBeat(services));
              }}
            >
              {text.t(m.mergeAtPlayhead)}
            </Button>
            <Select<MergeAlign>
              label={text.t(m.mergeAlign)}
              value={align}
              options={MERGE_ALIGNS.map((a) => ({ value: a, label: text.t(ALIGN_TITLES[a]) }))}
              onChange={setAlign}
            />
          </div>
        )}
      </div>
    </Dialog>
  );
}
